import { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Download,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useAccounting,
  useUploadFactura,
  useSaveApunte,
  useDeleteApunte,
  categoriasUsadas,
  type Apunte,
  type TipoApunte,
} from '../api/accounting';
import { useCommissions } from '../api/commissions';
import CategoryCombobox from '../components/CategoryCombobox';
import { descargarLibroCsv } from '../lib/librocsv';
import { descargarLibroXlsx } from '../lib/libroxlsx';
import { importeApunte } from '../lib/importe';
import { AyudaGuia } from '../components/Ayuda';

const eur = (n?: number) => `${(n ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const TRIMS = ['T1', 'T2', 'T3', 'T4'];
const triDeMes = (mes: string) => {
  const [y, m] = mes.split('-').map(Number);
  return y && m ? `${y}-T${Math.floor((m - 1) / 3) + 1}` : '';
};
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export default function ContabilidadView() {
  const anioActual = String(new Date().getFullYear());
  const [anio, setAnio] = useState(anioActual);
  const [manual, setManual] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, error, refetch } = useAccounting(anio);
  const { data: comisiones } = useCommissions();
  const subir = useUploadFactura();

  const items = data?.items ?? [];
  const sugeridas = data?.categorias_sugeridas;
  const usadas = useMemo(() => categoriasUsadas(items), [items]);

  const porRevisar = items.filter(
    (a) => a.estado === 'procesando' || a.estado === 'pending_review' || a.estado === 'validation_failed' || a.estado === 'error',
  );
  const confirmados = items.filter((a) => a.estado === 'confirmed');

  const comisAnio = (comisiones ?? []).filter((c) => c.mes?.startsWith(anio));
  const ingresosApuntes = confirmados.filter((a) => a.tipo === 'ingreso').reduce((s, a) => s + importeApunte(a), 0);
  const comisTotal = comisAnio.reduce((s, c) => s + (c.importe ?? 0), 0);
  const ingresos = ingresosApuntes + comisTotal;
  const gastos = confirmados.filter((a) => a.tipo === 'gasto').reduce((s, a) => s + importeApunte(a), 0);

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files)) await subir.mutateAsync(f);
    refetch();
  };

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-deep">Contabilidad</h1>
            <AyudaGuia seccion="contabilidad" />
          </div>
          <p className="mt-1 text-sm text-ink/55">
            Libro de ingresos y gastos. Sube una factura y la IA la lee, o añádela a
            mano; luego lo exportas para la gestoría.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={subir.isPending}
            className="flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-60"
          >
            {subir.isPending ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Subir factura
          </button>
          <button
            onClick={() => setManual(true)}
            className="flex items-center gap-2 rounded-full border border-deep/15 bg-surface px-4 py-2.5 text-sm font-bold text-deep transition hover:border-deep/30"
          >
            <Plus size={16} /> A mano
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => onFiles(e.target.files)} />

      {/* Año + export */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full bg-soft p-1">
          {aniosDisponibles(items, comisiones ?? [], anioActual).map((y) => (
            <button
              key={y}
              onClick={() => setAnio(y)}
              className={clsx(
                'rounded-full px-4 py-1.5 text-sm font-bold transition',
                anio === y ? 'bg-brand text-white' : 'text-ink/55 hover:text-deep',
              )}
            >
              {y}
            </button>
          ))}
        </div>
        {/* Excel para la gestoría (cifras como números, dos hojas); el CSV sigue
            para quien lo prefiera. Los dos salen de los mismos datos (lib/libro.ts). */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => descargarLibroXlsx(anio, confirmados, comisAnio)}
            className="flex items-center gap-2 rounded-full bg-deep px-4 py-2 text-sm font-bold text-white transition hover:bg-brand"
          >
            <Download size={15} /> Exportar Excel
          </button>
          <button
            onClick={() => descargarLibroCsv(anio, confirmados, comisAnio)}
            className="flex items-center gap-2 rounded-full border border-deep/15 bg-surface px-4 py-2 text-sm font-bold text-deep transition hover:border-deep/30"
          >
            Exportar CSV
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Ingresos" value={eur(ingresos)} tone="pos" />
        <Kpi label="Gastos" value={eur(gastos)} tone="neg" />
        <Kpi label="Resultado" value={eur(ingresos - gastos)} />
        <Kpi label="Por revisar" value={String(porRevisar.length)} />
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError ? (
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se ha podido cargar la contabilidad</p>
          <p className="mt-1 text-sm text-red-600/80">{(error as Error)?.message}</p>
          <button onClick={() => refetch()} className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700">
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {porRevisar.length > 0 && (
            <section className="mt-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-brand">
                Por revisar · {porRevisar.length}
              </h2>
              <div className="mt-3 space-y-3">
                {porRevisar.map((a) => (
                  <RevisarCard key={a.doc_id} apunte={a} categorias={usadas} sugeridas={sugeridas} />
                ))}
              </div>
            </section>
          )}

          <Libro anio={anio} confirmados={confirmados} comisiones={comisAnio} />
        </>
      )}

      {manual && <ManualModal onClose={() => setManual(false)} categorias={usadas} sugeridas={sugeridas} />}
    </div>
  );
}

// ── El libro por trimestre ────────────────────────────────────────

function Libro({ anio, confirmados, comisiones }: { anio: string; confirmados: Apunte[]; comisiones: { commission_id: string; concepto: string; importe: number; mes: string }[] }) {
  const suma = (xs: Apunte[]) => xs.reduce((s, a) => s + importeApunte(a), 0);
  const hayAlgo = confirmados.length > 0 || comisiones.length > 0;

  if (!hayAlgo) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-12 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-leaf/40 text-deep">
          <FileText size={22} strokeWidth={1.75} />
        </div>
        <p className="mt-4 font-bold text-deep">El libro de {anio} está vacío</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink/60">
          Sube una factura o añade un apunte a mano. Tus comisiones, de operaciones y de
          servicios, aparecerán aquí como ingresos.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-6">
      {TRIMS.map((t) => {
        const tri = `${anio}-${t}`;
        const ingr = confirmados.filter((a) => a.tipo === 'ingreso' && a.trimestre === tri);
        const gast = confirmados.filter((a) => a.tipo === 'gasto' && a.trimestre === tri);
        const comisTri = comisiones.filter((c) => triDeMes(c.mes) === tri);
        const comisSuma = comisTri.reduce((s, c) => s + (c.importe ?? 0), 0);
        if (ingr.length === 0 && gast.length === 0 && comisTri.length === 0) return null;
        const totIngr = suma(ingr) + comisSuma;
        const totGast = suma(gast);
        return (
          <section key={t}>
            <div className="flex items-baseline justify-between border-b border-deep/10 pb-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink/55">{tri}</h2>
              <span className="text-sm font-extrabold text-deep">
                Resultado {eur(totIngr - totGast)}
              </span>
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Columna titulo="Ingresos" total={totIngr} tone="pos">
                {comisTri.length > 0 && (
                  <FilaApunte concepto={`Comisiones · ${comisTri.length}`} categoria="Comisiones" total={comisSuma} />
                )}
                {ingr.map((a) => (
                  <FilaApunte key={a.doc_id} concepto={a.concepto || 'Ingreso'} categoria={a.categoria} total={importeApunte(a)} irpf={a.irpf_amount} />
                ))}
                {ingr.length === 0 && comisTri.length === 0 && <Vacio />}
              </Columna>
              <Columna titulo="Gastos" total={totGast} tone="neg">
                {gast.map((a) => (
                  <FilaApunte key={a.doc_id} concepto={a.concepto || 'Gasto'} categoria={a.categoria} total={importeApunte(a)} irpf={a.irpf_amount} />
                ))}
                {gast.length === 0 && <Vacio />}
              </Columna>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Columna({ titulo, total, tone, children }: { titulo: string; total: number; tone: 'pos' | 'neg'; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wider text-ink/45">{titulo}</h3>
        <span className={clsx('text-sm font-extrabold', tone === 'pos' ? 'text-emerald-700' : 'text-deep')}>{eur(total)}</span>
      </div>
      <ul className="mt-2 space-y-1.5">{children}</ul>
    </div>
  );
}

function FilaApunte({ concepto, categoria, total, irpf }: { concepto: string; categoria?: string; total?: number; irpf?: number }) {
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <div className="min-w-0">
        <p className="truncate text-deep">{concepto}</p>
        {(categoria || irpf) && (
          <p className="text-xs text-ink/45">
            {[categoria, irpf ? `retención IRPF ${eur(irpf)}` : ''].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      <span className="shrink-0 font-bold text-deep">{eur(total)}</span>
    </li>
  );
}

const Vacio = () => <li className="text-sm text-ink/40">—</li>;

// ── Tarjeta de revisión (editable) ────────────────────────────────

function RevisarCard({ apunte, categorias, sugeridas }: { apunte: Apunte; categorias: string[]; sugeridas?: Record<TipoApunte, string[]> }) {
  const guardar = useSaveApunte();
  const borrar = useDeleteApunte();
  const [f, setF] = useState<Partial<Apunte>>({
    tipo: apunte.tipo ?? 'gasto',
    categoria: apunte.categoria ?? '',
    concepto: apunte.concepto ?? '',
    contraparte: apunte.contraparte ?? '',
    fecha: apunte.fecha ?? '',
    base_imponible: apunte.base_imponible,
    iva_pct: apunte.iva_pct,
    iva_amount: apunte.iva_amount,
    irpf_pct: apunte.irpf_pct,
    irpf_amount: apunte.irpf_amount,
    total: apunte.total,
  });

  const procesando = apunte.estado === 'procesando';
  const opciones = mezclarCategorias(categorias, sugeridas, f.tipo as TipoApunte);

  if (procesando) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-deep/10 bg-surface px-4 py-4 text-sm text-ink/60">
        <Loader2 size={16} className="animate-spin text-brand" />
        Leyendo la factura con IA…
      </div>
    );
  }

  const confirmar = () =>
    guardar.mutate({ doc_id: apunte.doc_id, ...f, estado: 'confirmed' });

  return (
    <div className="rounded-2xl border border-deep/10 bg-surface p-4">
      {apunte.estado === 'validation_failed' && (
        <p className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700">
          <AlertTriangle size={13} /> Los importes no cuadran — revísalos antes de confirmar.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo label="Tipo">
          <select
            value={f.tipo}
            onChange={(e) => setF({ ...f, tipo: e.target.value as TipoApunte })}
            className="w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep"
          >
            <option value="gasto">Gasto</option>
            <option value="ingreso">Ingreso</option>
          </select>
        </Campo>
        <Campo label="Categoría">
          <CategoryCombobox value={f.categoria ?? ''} options={opciones} onChange={(v) => setF({ ...f, categoria: v })} />
        </Campo>
        <Campo label="Concepto" full>
          <Input value={f.concepto ?? ''} onChange={(v) => setF({ ...f, concepto: v })} />
        </Campo>
        <Campo label="Contraparte">
          <Input value={f.contraparte ?? ''} onChange={(v) => setF({ ...f, contraparte: v })} />
        </Campo>
        <Campo label="Fecha (AAAA-MM-DD)">
          <Input value={f.fecha ?? ''} onChange={(v) => setF({ ...f, fecha: v })} placeholder="2026-08-25" />
        </Campo>
        <Campo label="Base imponible €">
          <NumInput value={f.base_imponible} onChange={(v) => setF({ ...f, base_imponible: v })} />
        </Campo>
        <Campo label="IVA %">
          <NumInput value={f.iva_pct} onChange={(v) => setF({ ...f, iva_pct: v })} />
        </Campo>
        <Campo label="IVA €">
          <NumInput value={f.iva_amount} onChange={(v) => setF({ ...f, iva_amount: v })} />
        </Campo>
        <Campo label="Retención IRPF %">
          <NumInput value={f.irpf_pct} onChange={(v) => setF({ ...f, irpf_pct: v })} />
        </Campo>
        <Campo label="Retención IRPF €">
          <NumInput value={f.irpf_amount} onChange={(v) => setF({ ...f, irpf_amount: v })} />
        </Campo>
        <Campo label="Total €">
          <NumInput value={f.total} onChange={(v) => setF({ ...f, total: v })} />
        </Campo>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={() => borrar.mutate(apunte.doc_id)}
          className="flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700"
        >
          <Trash2 size={15} /> Eliminar
        </button>
        <button
          onClick={confirmar}
          disabled={guardar.isPending}
          className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white transition hover:bg-deep disabled:opacity-60"
        >
          Confirmar
        </button>
      </div>
    </div>
  );
}

// ── Alta manual ───────────────────────────────────────────────────

function ManualModal({ onClose, categorias, sugeridas }: { onClose: () => void; categorias: string[]; sugeridas?: Record<TipoApunte, string[]> }) {
  const guardar = useSaveApunte();
  const [f, setF] = useState<Partial<Apunte>>({ tipo: 'gasto', categoria: '', concepto: '', contraparte: '', fecha: new Date().toISOString().slice(0, 10) });
  const opciones = mezclarCategorias(categorias, sugeridas, f.tipo as TipoApunte);

  const submit = () => {
    if (!f.concepto?.trim() || !f.fecha?.trim()) return;
    guardar.mutate(f, { onSuccess: onClose });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-t-3xl bg-white p-6 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-deep">Añadir apunte a mano</h2>
          <button onClick={onClose} className="text-ink/40 hover:text-deep"><X size={20} /></button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Campo label="Tipo">
            <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value as TipoApunte })} className="w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep">
              <option value="gasto">Gasto</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </Campo>
          <Campo label="Categoría">
            <CategoryCombobox value={f.categoria ?? ''} options={opciones} onChange={(v) => setF({ ...f, categoria: v })} />
          </Campo>
          <Campo label="Concepto *" full>
            <Input value={f.concepto ?? ''} onChange={(v) => setF({ ...f, concepto: v })} />
          </Campo>
          <Campo label="Contraparte">
            <Input value={f.contraparte ?? ''} onChange={(v) => setF({ ...f, contraparte: v })} />
          </Campo>
          <Campo label="Fecha (AAAA-MM-DD) *">
            <Input value={f.fecha ?? ''} onChange={(v) => setF({ ...f, fecha: v })} placeholder="2026-08-25" />
          </Campo>
          <Campo label="Base imponible €">
            <NumInput value={f.base_imponible} onChange={(v) => setF({ ...f, base_imponible: v })} />
          </Campo>
          <Campo label="IVA %">
            <NumInput value={f.iva_pct} onChange={(v) => setF({ ...f, iva_pct: v })} />
          </Campo>
          <Campo label="IVA €">
            <NumInput value={f.iva_amount} onChange={(v) => setF({ ...f, iva_amount: v })} />
          </Campo>
          <Campo label="Retención IRPF %">
            <NumInput value={f.irpf_pct} onChange={(v) => setF({ ...f, irpf_pct: v })} />
          </Campo>
          <Campo label="Retención IRPF €">
            <NumInput value={f.irpf_amount} onChange={(v) => setF({ ...f, irpf_amount: v })} />
          </Campo>
          <Campo label="Total €">
            <NumInput value={f.total} onChange={(v) => setF({ ...f, total: v })} />
          </Campo>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-bold text-ink/55 hover:text-deep">Cancelar</button>
          <button onClick={submit} disabled={guardar.isPending} className="rounded-full bg-brand px-5 py-2 text-sm font-bold text-white hover:bg-deep disabled:opacity-60">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Piezas ────────────────────────────────────────────────────────

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <p className={clsx('text-2xl font-extrabold', tone === 'pos' ? 'text-emerald-700' : 'text-deep')}>{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
    </div>
  );
}

function Campo({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <label className={clsx('block', full && 'sm:col-span-2')}>
      <span className="mb-1 block text-xs font-bold text-ink/50">{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none placeholder:text-ink/40 focus:border-brand"
    />
  );
}

function NumInput({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
  return (
    <input
      type="number"
      step="0.01"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      className="w-full rounded-xl border border-deep/15 bg-surface px-3 py-2 text-sm text-deep outline-none focus:border-brand"
    />
  );
}

// ── Utilidades ────────────────────────────────────────────────────

function mezclarCategorias(usadas: string[], sugeridas: Record<TipoApunte, string[]> | undefined, tipo: TipoApunte): string[] {
  const base = sugeridas?.[tipo] ?? [];
  return Array.from(new Set([...usadas, ...base]));
}

function aniosDisponibles(items: Apunte[], comisiones: { mes: string }[], actual: string): string[] {
  const set = new Set<string>([actual]);
  for (const a of items) if (a.anio) set.add(a.anio);
  for (const c of comisiones) if (c.mes) set.add(c.mes.slice(0, 4));
  return Array.from(set).sort((a, b) => b.localeCompare(a));
}
